import { v4 as uuidv4 } from 'uuid';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

import type { RemoteEvaluationClient } from '@amplitude/experiment-node-server';
import type { Request, Response } from 'express';
import type { Cookies } from 'react-cookie';

const logger = Logger.getLogger('wanderu-analytics');

let experimentServer: RemoteEvaluationClient;

function getShouldSkipAssignment(req: Request): [boolean, string | null, Record<string, unknown>] {
    const userAgent = req?.headers?.['user-agent'] || '';

    // Check Cloudflare headers
    const cfBotScore = Number(req?.headers?.['cf-bot-score']);
    if (cfBotScore && cfBotScore < 30) return [true, 'Cloudflare bot score', { cfBotScore }];

    // Check for missing/suspicious headers
    if (!userAgent || !req?.headers?.['accept-language']) return [true, 'missing/suspicious headers', { acceptLanguage: req?.headers?.['accept-language'], userAgent }];

    // User-agent patterns (leaving out for now, since ideally we don't control this in-repo)
    // if (isBotUserAgent(userAgent)) return true;

    // Health check endpoints
    if (req?.path === '/readyz' || req?.path === '/livez' || req?.path === '/ping') return [true, 'health check endpoints', { path: req?.path }];

    // Missing critical browser headers
    const hasAccept = req?.headers?.accept;
    if (!hasAccept || !hasAccept.includes('text/html')) return [true, 'missing critical browser headers', { accept: req?.headers?.accept }];

    return [false, null, {}];
}

export async function fetchServerVariants(req: Request, res: Response, cookies: Cookies) {
    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const isServerSide = typeof window === 'undefined';

    if (!apiKey || !isServerSide) {
        return undefined;
    }

    const deviceId = await handleAmplitudeCookies(cookies, res);

    const [shouldSkipAssignment, skipReason, properties] = getShouldSkipAssignment(req);

    logger.event('shouldSkipAssignment', {
        deviceId,
        shouldSkipAssignment,
        skipReason,
        ...properties,
    });

    if (!experimentServer) {
        const config = {
            fetchRetries: 1,
            fetchRetryBackoffMinMillis: 0,
            fetchRetryTimeoutMillis: 500,
            fetchTimeoutMillis: 500,
        };

        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        experimentServer = require('@amplitude/experiment-node-server')
            .Experiment
            .initializeRemote(apiKey, config);
    }

    // turning off for now to test bot detection
    // const variants = await experimentServer
    //     .fetchV2({ device_id: deviceId });

    const variants = {};

    return variants;
}

async function handleAmplitudeCookies(cookies: Cookies, res: Response) { // https://amplitude.com/docs/sdks/experiment-sdks/experiment-node-js#access-amplitude-cookies
    let deviceId: string | undefined;

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
    const { AmplitudeCookie } = require('@amplitude/experiment-node-server');

    // Get the cookie name for the Amplitude API key
    const ampCookieName = AmplitudeCookie.cookieName(apiKey, true);

    const cookie = cookies.get(ampCookieName);
    // Try to get device ID from existing cookie
    if (cookie) {
        deviceId = AmplitudeCookie.parse(cookie, true).device_id;
    }

    // If no device ID found, generate a new one and set the cookie
    if (!deviceId) {
        deviceId = uuidv4();

        const ampCookieValue = AmplitudeCookie.generate(deviceId, true);

        cookies.set(ampCookieName, ampCookieValue);
        res.cookie(ampCookieName, ampCookieValue, {
            domain: '.wanderu.com', // this should be the same domain used by the Amplitude JS SDK
            httpOnly: false,
            secure: false,
        });
    }

    return deviceId;
}
